import {
  useState,
  createContext,
  useContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useLayoutEffect,
} from 'react'
import {
  AssetType,
  MarketplaceAssetType,
  MarketplaceInfoResume,
  Sale,
} from 'core/logic/asset/asset.types'
import usePagination, {
  Paginable,
  useLocalPagination,
} from '@onepercentio/one-ui/hooks/usePagination'
import { useBatchBalances, useOnChainOffers } from 'core/logic/token/token.hook'
import { useAssets } from 'core/logic/asset/asset.hook'
import { OnChainOfferType, StableTokenData } from 'core/logic/token/token.types'
import { normalizePrice } from 'core/helpers/contract'
import useRouteGalleryOrDefault from 'openspace/hooks/useRoutePathGallery'
import BigNumber from 'bignumber.js'
import { useWeb3Providers } from 'hooks/useWeb3Provider'
import { useChain, useStable, useStableContracts } from './Chain'
import OnchainMarketplace from 'service/IMarketplace/OnchainMarketplace'
import { useUser } from './User'
import useWeb3Utils from 'hooks/useWeb3Utils'
import IMarketplace from 'service/IMarketplace'
import ZeroXMarketplace, {
  CustodyZeroXMarketplace,
  ExternalZeroXMarketplace,
} from 'service/IMarketplace/ZeroXMarketplace'
import { useGalleryContext } from './Gallery'
import { useWallet } from './Wallet'
import {
  SALES_AMOUNT,
  TOKEN_BALANCE_FOR_ACCOUNTS,
  useIsGraphQlEnabled,
} from 'core/modules/graphql'
import { useQuery } from '@apollo/client/react'
import useUniqueEffect from '@onepercentio/one-ui/hooks/utility/useUniqueEffect'
import {
  CustodyEscrowMarketplace,
  ExternalEscrowMarketplace,
} from 'service/IMarketplace/EscrowMarketplace'
import { useAssetFilter } from 'components/AssetFilters/AssetFilters.hook'
import { BlockchainCurrenciesEnum } from 'core/types/CurrenciesEnum'
import useAsyncMemo from '@onepercentio/one-ui/hooks/utility/useAsyncMemo'
import { useAssetsBy } from './Asset'
import useFreeze from '@onepercentio/one-ui/hooks/useFreeze'

type IndexableBy = [AssetType['id'] | AssetType['id'][] | undefined]
export type MarketplaceContextShape = {
  currencies: CryptoCurrencySymbol[]

  currencyDetails: {
    [k: CryptoCurrencySymbol]: {
      address: string
      decimals: number
    }
  }
  /**
   * Used for the listing of the marketplace
   */
  salesResume:
    | {
        [assetId in AssetType['id']]: MarketplaceInfoResume | undefined
      }
    | undefined

  /**
   * Indicates if the sales resume are loading or error
   */
  salesResumeControl: {
    loading: boolean
    error: string | undefined
    onRetry: () => void
  }

  /**
   * Used for the listing when entering the details of a sale
   */
  sales: Paginable<Sale, []>

  tax: number | undefined

  salesList: (Sale & { assetId: string })[] | undefined
}
export const MarketplaceContext = createContext<MarketplaceContextShape>(
  null as any
)

function _useMarketplaceSalesFromOffers(
  offers: OnChainOfferType[] | undefined
) {
  const { stableInfo, stableList } = useStable()

  /** @optimization remove the query to all assets since the marketplace is not required everytime */
  // const { assets } = useAssets()
  const assets = useAssetsForOffers(offers)

  /** The sellers and the sold tokenIds (what about 721?) */
  const offerSellersAndTokenIds = useMemo(() => {
    if (offers) {
      return {
        accounts: offers.map((offer) => offer.paymentOptions.beneficiary),
        ids: offers.map((offer) => offer.deliveryOptions.tokenId.toString()),
      }
    }
    return { accounts: [], ids: [] }
  }, [offers])

  const [assetSalesList, setAssetSalesMap] =
    useState<MarketplaceContextShape['sales']['items']>()
  const [salesSummary, setSalesSummary] = useState<{
    [id: string]: MarketplaceInfoResume
  }>({})

  useEffect(() => {
    if (offers && assets && stableList) {
      //Offers summary
      let assetSummaryMap: { [id: string]: MarketplaceInfoResume } = {}
      let assetListMap: { [id: string]: Sale[] } = {}
      assets.forEach((asset) => {
        const lowestPriceByToken: { [symbol: string]: { lowest: number } } = {}
        let activeOffersCountByToken: number = 0
        let totalOffersCountByToken: number = 0

        const offersByTokenId = offers.filter((offer) => {
          if ('cid' in offer.deliveryOptions)
            return offer.deliveryOptions.cid === asset.ipfs.jsonHash
          return offer!.deliveryOptions.tokenId === asset.tokenId
        })
        if (!offersByTokenId.length) return

        stableList.forEach((token: StableTokenData) => {
          const tokenOffers = offersByTokenId.filter(
            (offer) =>
              stableInfo[offer!.paymentOptions.paymentToken]?.symbol ===
              token.symbol
          )

          if (offersByTokenId.length) {
            const lowest = tokenOffers.sort(
              (a, b) => a!.paymentOptions.price - b!.paymentOptions.price
            )[0]
            lowestPriceByToken[token.symbol] = {
              lowest: normalizePrice(
                lowest?.paymentOptions.price!,
                stableInfo[lowest?.paymentOptions.paymentToken!].decimals
              ),
            }
          }
        })
        activeOffersCountByToken = offersByTokenId.filter(
          (offer) => offer!.active
        ).length

        totalOffersCountByToken = offersByTokenId.length

        assetSummaryMap[asset.id] = {
          price: lowestPriceByToken,
          remaining: activeOffersCountByToken,
          supply: totalOffersCountByToken,
          marketplace: true,
        }
        if (!assetListMap[asset.id]) assetListMap[asset.id] = []
        assetListMap[asset.id].push(
          ...offersByTokenId.reduce((offers, offer) => {
            return [
              ...offers,
              ...new Array(offer.supply - offer.totalSold!).fill(undefined).map(
                () =>
                  ({
                    assetId: asset.id,
                    price: normalizePrice(
                      offer.paymentOptions.price,
                      stableInfo[offer.paymentOptions.paymentToken].decimals
                    ),
                    user:
                      offer.metadata?.firebaseUID ||
                      offer.paymentOptions.beneficiary,
                    wallet: offer.paymentOptions.beneficiary,
                    offerId: offer.id,
                    currency: stableInfo[offer.paymentOptions.paymentToken]
                      .symbol as BlockchainCurrenciesEnum,
                    tokenId: offer.deliveryOptions.tokenId,
                    remaining: offer.supply - offer.totalSold!,
                    deliveryId: offer.metadata?.deliveryId,
                    comments: offer.metadata?.comments,
                  } as Sale)
              ),
            ]
          }, [] as Sale[])
        )
      })
      setAssetSalesMap(
        Object.values(assetListMap)
          .reduce((acc, salesForAsset) => [...acc, ...salesForAsset], [])
          .sort((a, b) => {
            return (
              offers.findIndex((o) => o.id === a.offerId) -
              offers.findIndex((o) => o.id === b.offerId)
            )
          })
      )
      setSalesSummary(assetSummaryMap)
    }
  }, [offers, assets, stableList])

  return {
    assetSalesList,
    salesSummary,
    offerSellersAndTokenIds,
    assets,
  }
}

export function MarketplaceProvider({ children }: PropsWithChildren<{}>) {
  /** Get the stable tokens list */
  const { stableInfo, stableList } = useStable()
  const currentGallery = useRouteGalleryOrDefault()
  const marketplace = useMarketplaceInterface()

  const { offers, fee, refreshOffers } = useOnChainOffers(marketplace)

  const { assetSalesList, salesSummary, offerSellersAndTokenIds } =
    _useMarketplaceSalesFromOffers(offers)

  /** It will use the gallery config for the marketplace if is on route */
  const contractConfig = useRouteGalleryOrDefault()

  /** @optimization We should not be querying the balances of all users for all offers as it is not needed */
  const { batchBalances } = useBatchBalances(
    offerSellersAndTokenIds.accounts,
    offerSellersAndTokenIds.ids,
    contractConfig?.smartContract?.address,
    contractConfig?.smartContract?.type
  )

  return (
    <MarketplaceContext.Provider
      value={{
        currencies:
          stableList
            ?.filter((stable) =>
              currentGallery.marketplace?.stables
                ? currentGallery.marketplace?.stables.includes(stable.symbol)
                : true
            )
            .map((token) => token.symbol) || [],
        currencyDetails: stableInfo || {},
        sales: {
          getAll() {
            refreshOffers()
          },
          getNextPage() {
            refreshOffers()
          },
          items: assetSalesList,
          sellerHasBalance(seller: string, tokenId: string) {
            for (let i in batchBalances) {
              if (i === seller) {
                const idx: number | undefined = batchBalances[
                  i
                ].tokens?.findIndex((id) => id === tokenId)
                if (idx !== -1 && idx !== undefined) {
                  return batchBalances?.[i]?.balances?.[idx] === '0'
                } else {
                  return false
                }
              }
            }
            return false
          },
        } as any,
        salesResume: salesSummary,
        salesResumeControl: {
          loading: false,
          error: undefined,
          onRetry: () => {},
        },
        tax: fee,
        salesList: assetSalesList,
      }}>
      {children}
    </MarketplaceContext.Provider>
  )
}

export function useMarketplace() {
  return useContext(MarketplaceContext)
}

export function useMarketplaceAssets(itemsPerPage: number) {
  const { salesResume, currencies } = useMarketplace()
  const { assets } = useAssets()
  const gallery = useRouteGalleryOrDefault()

  const items = useMemo(() => {
    const marketplaceAssets = assets
      ?.map((asset) => {
        if (gallery && asset.galleryId !== gallery.id) return undefined
        const salesResumeForAsset = salesResume && salesResume[asset.id]
        if (salesResumeForAsset) {
          const currency = currencies[0]
          const priceInformationByCurrency = salesResumeForAsset.price[currency]
          if (priceInformationByCurrency) {
            const marketplaceResume: MarketplaceInfoResume = {
              ...salesResumeForAsset,
              price: {
                [currencies[0]]: {
                  lowest: priceInformationByCurrency!.lowest,
                },
              },
            }
            return {
              ...asset,
              ...marketplaceResume,
              marketplace: true,
            }
          }
        }
        return undefined
      })
      .filter(Boolean) as MarketplaceAssetType[]

    return marketplaceAssets
      ? [
          ...marketplaceAssets,
          ...assets!.filter((a) => {
            const assetHasMarketplaceOffer = marketplaceAssets.find(
              (m) => m.id === a.id
            )
            return !assetHasMarketplaceOffer
          }),
        ]
      : undefined
  }, [assets, currencies, gallery, salesResume])

  const paginable = useLocalPagination(items)

  useEffect(() => {
    paginable.getPage(0, itemsPerPage * 2)
  }, [items])

  return paginable
}

export function useAssetsForOffers(offers: OnChainOfferType[] = []) {
  const { cids, tokenIds } = useMemo(() => {
    return offers.reduce(
      (acc, offer) => {
        if (offer.deliveryOptions.cid) acc.cids.push(offer.deliveryOptions.cid)
        else acc.tokenIds.push(offer.deliveryOptions.tokenId)
        return acc
      },
      { cids: [] as string[], tokenIds: [] as number[] }
    )
  }, [offers])
  const cidAssets = useAssetsBy('cid', cids)
  const tokenIdAssets = useAssetsBy('token', tokenIds)

  const allAssets = useMemo(
    () => [...cidAssets.assets, ...tokenIdAssets.assets],
    [cidAssets.assets, tokenIdAssets.assets]
  )

  return allAssets
}

export function useMarketplaceSale(offerId: Sale['offerId']) {
  const { salesList, sales } = useMarketplace()

  useEffect(() => {
    if (salesList === undefined) sales.getAll()
  }, [salesList])

  return salesList?.find((s) => s.offerId === offerId)
}

function useSaleWithTotalAmount(offerId: string) {
  const Imarket = useMarketplaceInterface()
  const [sale] = useAsyncMemo(
    async () => (Imarket ? Imarket.getSpecificOffer(offerId) : undefined),
    [Imarket, offerId]
  )
  const items = useMemo(() => (!sale ? undefined : [sale]), [sale])
  const { offersWithUnits } = useMarketplaceSales(items)
  return offersWithUnits
}

function parseOnChainOfferToSale(
  normalizedOffers: ReturnType<typeof useSaleWithTotalAmount>
) {
  return _useMarketplaceSalesFromOffers(normalizedOffers).assetSalesList?.[0]
}

export function useSaleAvailableOnMarketplace(offerId: Sale['offerId']) {
  const normalizedOffers = useSaleWithTotalAmount(offerId)
  const normalizedSale = parseOnChainOfferToSale(normalizedOffers)
  return normalizedSale
}

export function useSaleInformation(offerId: Sale['offerId']) {
  const offersWithUnits = useSaleWithTotalAmount(offerId)
  const normalizedSale = parseOnChainOfferToSale(offersWithUnits)

  return normalizedSale
}

export function useMarketplaceInterface() {
  const { readWeb3, writeWeb3 } = useWeb3Providers()
  const contractConfig = useRouteGalleryOrDefault()
  const { galleries } = useGalleryContext()
  const { profile } = useUser()
  const { mode } = useWallet()
  const stableContracts = useStableContracts()
  const web3Utils = useWeb3Utils()
  const chain = useChain()
  const { stableInfo } = useStable()
  const [marketplace, setMarketplace] = useState<IMarketplace>()

  useEffect(() => {
    if (
      readWeb3 &&
      contractConfig &&
      stableInfo &&
      contractConfig.marketplace
    ) {
      const marketplaceType = contractConfig.marketplace.type
      switch (marketplaceType) {
        case '0x':
        case 'escrow':
          if (chain && galleries) {
            const instance =
              marketplaceType === 'escrow'
                ? mode === 'provider'
                  ? new ExternalEscrowMarketplace()
                  : new CustodyEscrowMarketplace()
                : mode === 'provider'
                ? new ExternalZeroXMarketplace()
                : new CustodyZeroXMarketplace()
            instance.setChainId(chain.id)
            instance.setChain(readWeb3, chain)
            instance.setWeb3Provider(readWeb3, writeWeb3)
            instance.setGalleries(galleries)
            instance.setWallet(profile?.wallet).then(() => {
              setMarketplace(instance)
            })
          }
          break
        case 'rarum':
          setMarketplace(new OnchainMarketplace())
          break
      }
    }
  }, [contractConfig, readWeb3, profile?.wallet])

  useEffect(() => {
    if (readWeb3 && writeWeb3)
      if (marketplace)
        if (marketplace instanceof OnchainMarketplace)
          marketplace.setWeb3Provider(readWeb3, writeWeb3)
  }, [readWeb3, writeWeb3, marketplace])

  useEffect(() => {
    if (marketplace)
      if (contractConfig.smartContract?.address)
        if (marketplace instanceof OnchainMarketplace)
          marketplace.setSalesContract(contractConfig.smartContract.address)
  }, [profile])

  useLayoutEffect(() => {
    const stableMap: Parameters<
      OnchainMarketplace['setAvailableCurrencies']
    >[0] = {}

    for (let stableSymbol in stableInfo) {
      if (stableContracts[stableSymbol])
        stableMap[stableSymbol] = {
          contract: stableContracts[stableSymbol].contract,
          ...stableInfo[stableSymbol],
        }
    }
    if (marketplace) marketplace.setAvailableCurrencies(stableMap)
  }, [stableInfo, stableContracts, marketplace])

  useEffect(() => {
    if (chain)
      if (marketplace instanceof OnchainMarketplace)
        marketplace.setChain(web3Utils, chain)
      else if (marketplace instanceof ZeroXMarketplace)
        marketplace.setChainId(chain!.id)
  }, [chain, web3Utils])

  return marketplace
}

/**
 * This function receives the available offers and normalize it's available
 * units amount according to the amount available at the sellers wallets
 */
export function useNormalizedOffers(offers: OnChainOfferType[] | undefined) {
  const hasGraphQl = useIsGraphQlEnabled()
  const offersOrEmptyArr = useMemo(() => offers ?? [], [offers])
  const { sellerWallets, tokenIds } = useMemo(() => {
    return {
      sellerWallets: offersOrEmptyArr
        .map((o) => o.metadata!.makerWallet.toLowerCase())
        .filter(Boolean),
      tokenIds: Array.from(
        new Set(offersOrEmptyArr.map((o) => String(o.deliveryOptions.tokenId)))
      ),
    }
  }, [offers])

  const { data } = useQuery<{
    tokenHolders: {
      amount: string
      token: {
        id: string
      }
      wallet: {
        id: string
      }
    }[]
  }>(TOKEN_BALANCE_FOR_ACCOUNTS, {
    variables: {
      wallets: sellerWallets,
      tokenIds: tokenIds,
    },
    skip: sellerWallets.length === 0 || tokenIds.length === 0,
  })

  const normalizedOffers = useMemo(() => {
    if (!hasGraphQl) return offers
    if (data && offers && data.tokenHolders) {
      return offers
        .map((offer) => {
          const availableTokenInUserWallet = data.tokenHolders.find(
            (holder) =>
              holder.wallet.id === offer.metadata!.makerWallet.toLowerCase() &&
              holder.token.id === String(offer.deliveryOptions.tokenId)
          )
          if (offer.supply === 0) return undefined
          return {
            ...offer,
            supply: availableTokenInUserWallet
              ? Math.min(
                  offer.supply,
                  Number(availableTokenInUserWallet.amount)
                )
              : 0,
          }
        })
        .filter(Boolean)
    } else return offers
  }, [offers, data])

  return normalizedOffers as OnChainOfferType[] | undefined
}

export function useReceivableAmount(tax: number | undefined, amount: number) {
  return useMemo(() => {
    if (tax === undefined) return undefined
    const valueBn = new BigNumber(amount)
    const taxBn = new BigNumber(tax).multipliedBy(100)
    const taxToFraction = new BigNumber(100).minus(taxBn).div(100)
    return valueBn.multipliedBy(taxToFraction).toNumber()
  }, [tax, amount])
}

export function useMarketplaceOffers() {
  const ctx = useMarketplace()

  useUniqueEffect(
    'marketplace_sales',
    () => {
      if (ctx.sales.items === undefined) ctx.sales.getAll()
    },
    [ctx.sales.items]
  )

  return ctx
}

/**
 * Get the total sold count from the zerox sold events
 * @param offers The registered offers
 * @returns The offers with the sold amount
 */
export function useMarketplaceSales(
  offers: OnChainOfferType[] | null | undefined
) {
  const hasGraphQl = useIsGraphQlEnabled()
  const offersWithoutRemainingUnits = useMemo(() => {
    return offers?.filter((offer) => offer.totalSold === undefined)
  }, [offers])
  const graphqlIds = useMemo(() => {
    return offersWithoutRemainingUnits?.map((o) => o.metadata!.id0x)
  }, [offersWithoutRemainingUnits])
  const { data, error, refetch } = useQuery<{
    sales: { id: string; sold: number }[]
  }>(SALES_AMOUNT, {
    variables: { ids: graphqlIds },
    fetchPolicy: 'network-only',
  })
  const sales = data?.sales

  const offersWithUnits = useMemo(() => {
    if (!hasGraphQl)
      return offers?.map((o) => ({ ...o, totalSold: o.totalSold || 0 }))
    if (offers && sales)
      return offers.map((offer) => {
        if (offer.totalSold) return offer
        else {
          const saleData = sales.find((s) => s.id === offer.metadata!.id0x)
          return {
            ...offer,
            totalSold: saleData?.sold ? Number(saleData?.sold) : 0,
          }
        }
      })
  }, [sales, offers])
  return {
    offersWithUnits,
    refreshSales: () => refetch(),
  }
}

export function useDynamicMarketplaceOffers(
  currentFilter: Partial<
    ReturnType<typeof useAssetFilter>[0] & {
      byUser: boolean
    }
  >
) {
  const relatedGallery = useRouteGalleryOrDefault()
  const marketplaceInterface = useMarketplaceInterface()
  const queryRunner = useMemo(() => {
    if (!marketplaceInterface || !relatedGallery)
      return () =>
        Promise.resolve({
          finished: true,
          items: [],
          totalItems: 0,
        })
    return marketplaceInterface.getQueryForCollectionOffers(
      relatedGallery.id,
      currentFilter
    )
  }, [currentFilter, marketplaceInterface, relatedGallery])

  const salesPaginationId = useMemo(
    () => Math.random().toString(),
    [queryRunner]
  )
  const salesPagination = usePagination(queryRunner, () => salesPaginationId)
  const frozenSalesItems = useFreeze(salesPagination.items)

  const { offersWithUnits, refreshSales } =
    useMarketplaceSales(frozenSalesItems)
  const normalizedOffers = useNormalizedOffers(offersWithUnits)

  const { assetSalesList, assets } =
    _useMarketplaceSalesFromOffers(normalizedOffers)

  return {
    sales: {
      ...salesPagination,
      items: assetSalesList,
    },
    assets,
  }
}
